import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import {
  emailsByCategory,
  type EmailCatalogEntry,
} from "../lib/data/email-catalog.js";
import {
  getMySettings,
  patchMySettings,
} from "../lib/api-modules/user-settings.js";
import {
  Card,
  Pill,
  SectionHeader,
} from "../components/design-system/index.js";
import { useToast } from "../components/toast.js";
import { useDocumentTitle } from "../lib/util/use-document-title.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * /settings/emails — Phase 13.5 Cleave 5.
 *
 * Lists every Hearth email send (per the static manifest in
 * `web/src/lib/data/email-catalog.ts`) with:
 *   - per-template opt-in toggle (persists in user_settings.email_opt_outs)
 *   - human-readable schedule + trigger Lambda
 *   - "Preview" button — fetches the SES HTML template via Vite ?raw +
 *     substitutes fixture variables + renders inside an iframe srcDoc
 *
 * Routing (email_routes) is schema-only in v1 — the field exists on
 * user_settings + the admin UI shows the dropdown, but no trigger
 * Lambda honors it yet (deferred to a 13.6+ pass that resolves
 * member_id → user email per audit). The toggle still persists.
 *
 * Editing template body + schedule expression are EXPLICITLY out of
 * scope for v1 — Phase 17+ ops-hardening.
 */

// Vite glob: bundle every template's HTML at build time so the preview
// iframe can render without a network round-trip. The `as: "raw"` form
// is officially supported by Vite for bundled file imports.
const TEMPLATE_HTML = import.meta.glob<string>(
  "/../infra/email/templates/*.html",
  { query: "?raw", import: "default", eager: true },
);
const FIXTURE_JSON = import.meta.glob<Record<string, unknown>>(
  "/../infra/email/fixtures/*.json",
  { import: "default", eager: true },
);

function templateNameFromPath(path: string): string {
  const m = path.match(/\/([^/]+)\.(html|json)$/);
  return m?.[1] ?? "";
}

function lookupTemplateHtml(template_name: string): string | undefined {
  for (const [path, html] of Object.entries(TEMPLATE_HTML)) {
    if (templateNameFromPath(path) === template_name) return html;
  }
  return undefined;
}

function lookupFixture(template_name: string): Record<string, unknown> | undefined {
  for (const [path, fixture] of Object.entries(FIXTURE_JSON)) {
    if (templateNameFromPath(path) === template_name) return fixture;
  }
  return undefined;
}

/**
 * Tiny mustache substitution — handles only `{{var}}` simple lookups.
 * We don't render `{{#each}}` / `{{#if}}` blocks in the preview because
 * they require a real template engine; the iframe shows raw block
 * markup which is acceptable for v1 (the user can see the structure).
 * Phase 17+ may upgrade to a full handlebars renderer.
 */
function renderPreview(html: string, fixture: Record<string, unknown>): string {
  return html.replace(/\{\{\s*([\w_.]+)\s*\}\}/g, (_, key: string) => {
    const v = fixture[key];
    if (v === undefined || v === null) return `{{${key}}}`;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      return String(v);
    }
    return JSON.stringify(v);
  });
}

const CATEGORY_LABELS: Record<string, string> = {
  transactional: "Transactional",
  digest: "Digests",
  behavioral: "Behavioral coaching",
  ops: "Ops & alerts",
};

export function SettingsEmailsPage() {
  useDocumentTitle("Settings — Emails");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [previewing, setPreviewing] = useState<EmailCatalogEntry | null>(null);

  const settingsQuery = useQuery({
    queryKey: ["me-settings"],
    queryFn: getMySettings,
    enabled: !!user,
  });

  const optMutation = useMutation({
    mutationFn: (email_opt_outs: Record<string, boolean>) =>
      patchMySettings({ email_opt_outs }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me-settings"] });
      toast.show("Email preferences saved");
    },
    onError: (err) =>
      toast.show(
        err instanceof Error
          ? `Couldn't save: ${err.message}`
          : "Couldn't save email preferences",
      ),
  });

  if (!user) return null;

  const optOuts = settingsQuery.data?.email_opt_outs ?? {};

  function toggleOptOut(opt_out_key: string, currentlyOptedOut: boolean) {
    const next = { ...optOuts, [opt_out_key]: !currentlyOptedOut };
    optMutation.mutate(next);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <SectionHeader
        title="Email admin"
        sub="Every email Hearth sends — when, why, and (for some) opt-out controls. Preview each template against sample data."
      />

      <Card>
        <p className="text-sm text-[var(--color-muted)]">
          Default for every email is opt-in. Transactional emails (welcome,
          invitations, spousal messages) cannot be opted out — they're load-
          bearing for the account UX. Schedule + body editing lives in the
          IaC for now (Phase 17+ adds in-app editing).
        </p>
      </Card>

      {(["transactional", "digest", "behavioral", "ops"] as const).map(
        (category) => {
          const entries = emailsByCategory(category);
          if (entries.length === 0) return null;
          return (
            <section key={category} className="space-y-3">
              <h2 className="font-display text-xl font-medium text-[var(--color-text)]">
                {CATEGORY_LABELS[category]}
              </h2>
              <div className="space-y-2">
                {entries.map((entry) => {
                  const optedOut = optOuts[entry.opt_out_key] === true;
                  return (
                    <Card key={entry.opt_out_key}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-2">
                            <p className="font-medium text-[var(--color-text)]">
                              {entry.title}
                            </p>
                            {!entry.opt_outable && (
                              <Pill tone="neutral">Required</Pill>
                            )}
                            {entry.routable && (
                              <Pill tone="accent">Household-wide</Pill>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-[var(--color-muted)]">
                            {entry.description}
                          </p>
                          <p className="mt-2 text-xs text-[var(--color-faint)]">
                            <strong>Schedule:</strong> {entry.schedule_human}
                          </p>
                          <p className="mt-0.5 text-xs text-[var(--color-faint)]">
                            <strong>Trigger:</strong>{" "}
                            <code className="text-[11px]">
                              {entry.trigger_lambda}
                            </code>
                          </p>
                          <p className="mt-0.5 text-xs text-[var(--color-faint)]">
                            <strong>Template:</strong>{" "}
                            <code className="text-[11px]">
                              {entry.template_name}
                            </code>
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {entry.opt_outable ? (
                            <button
                              type="button"
                              onClick={() =>
                                toggleOptOut(entry.opt_out_key, optedOut)
                              }
                              aria-pressed={!optedOut}
                              className={
                                !optedOut
                                  ? "rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-contrast)]"
                                  : "rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
                              }
                            >
                              {!optedOut ? "Receiving" : "Opted out"}
                            </button>
                          ) : (
                            <Pill tone="neutral">Always sent</Pill>
                          )}
                          <button
                            type="button"
                            onClick={() => setPreviewing(entry)}
                            className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
                          >
                            Preview
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          );
        },
      )}

      <SuggestedPromptsTile prompts={PAGE_PROMPTS.settingsEmails} />

      {previewing && (
        <PreviewPanel
          entry={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

function PreviewPanel({
  entry,
  onClose,
}: {
  readonly entry: EmailCatalogEntry;
  readonly onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const html = useMemo(() => lookupTemplateHtml(entry.template_name), [entry]);
  const fixture = useMemo(() => lookupFixture(entry.template_name), [entry]);
  const rendered = useMemo(() => {
    if (!html) return "";
    if (!fixture) return html;
    return renderPreview(html, fixture);
  }, [html, fixture]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col border-l border-[var(--color-border-default)] bg-[var(--color-surface)] shadow-xl"
        aria-label={`Preview of ${entry.title}`}
      >
        <header className="flex items-center justify-between border-b border-[var(--color-border-default)] px-5 py-4">
          <div>
            <h2 className="font-display text-lg font-medium text-[var(--color-text)]">
              {entry.title}
            </h2>
            <p className="text-xs text-[var(--color-muted)]">
              Preview rendered against{" "}
              <code className="text-[11px]">{entry.fixture_path}</code>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
            aria-label="Close preview"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-hidden bg-zinc-100 p-4 dark:bg-zinc-900">
          {html ? (
            <iframe
              title={`${entry.title} preview`}
              srcDoc={rendered}
              className="h-full w-full rounded-md border border-[var(--color-border-default)] bg-white"
              sandbox=""
            />
          ) : (
            <p className="text-sm text-[var(--color-danger)]">
              Couldn't load template HTML for{" "}
              <code>{entry.template_name}</code>.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}
